// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useMemo, useRef, useEffect } from 'react';

import { type DashboardFilterToApi, useEvolvePost, ignoreCanceledApiRequest } from '@evolvemep/api-ts';
import { Loader } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { AgGridContainer } from 'helpers/ag-grid/AgGridContainer';
import { baseColumnDef, baseGridDef } from 'helpers/ag-grid/baseColumnDef';
import { useSideBar } from 'helpers/ag-grid/useSideBar';

import { getWorkOrderColumnDefs } from './columnDefs';

type Props = {
  params: DashboardFilterToApi;
};

export const InsightsDetailedReport = ({ params }: Props) => {
  const gridRef = useRef<AgGridReact>(null);

  const { data, apiCall, loading } = useEvolvePost('shop/insights/workOrdersOverTimeDetail', {
    abortOnMoreRecent: true,
    abortOnUnmount: true,
  });

  useEffect(() => {
    void apiCall(params).catch(ignoreCanceledApiRequest);
  }, [apiCall, params]);

  const workOrderColumnDefs = useMemo(() => getWorkOrderColumnDefs(), []);

  const getColumnDef = useMemo(
    () => ({ rowData: data?.data ?? [], columnDefs: workOrderColumnDefs }),
    [data?.data, workOrderColumnDefs],
  );

  const sideBar = useSideBar({
    gridApi: gridRef.current?.api ?? null,
    tableName: 'shop-insights-work-orders',
    exportFileName: 'Work Orders',
    paginatedExport: false,
  });

  return (
    <AgGridContainer>
      {(applyWrap) => (
        <AgGridReact
          ref={gridRef}
          {...baseGridDef}
          onFirstDataRendered={applyWrap}
          defaultColDef={baseColumnDef}
          loadingOverlayComponent={() => <Loader size="lg" />}
          loading={loading}
          sideBar={sideBar}
          {...getColumnDef}
        />
      )}
    </AgGridContainer>
  );
};
